import React, { useEffect, useState, useRef } from "react";
import api from "../api";
import { Search } from "lucide-react";

/**
 * Reusable autocomplete: fetches options from an API endpoint with debounced query.
 * - fetcher(query) → Promise<Array<{value, label, subtitle?}>>
 */
export default function Autocomplete({
  value,
  onChange,
  placeholder,
  fetcher,
  initialOptions = [],
  testid,
  disabled = false,
  minChars = 0,
}) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState(initialOptions);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const debounceRef = useRef();
  const wrapperRef = useRef();

  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.length < minChars) { setOptions(initialOptions); return; }
    debounceRef.current = setTimeout(async () => {
      const opts = await fetcher(query);
      setOptions(opts);
      setHighlighted(0);
    }, 200);
    return () => clearTimeout(debounceRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const display = value ? (options.find(o => o.value === value)?.label || value) : "";

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted(h => Math.min(h + 1, options.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    if (e.key === "Enter" && options[highlighted]) {
      e.preventDefault();
      onChange(options[highlighted].value, options[highlighted]);
      setOpen(false);
      setQuery("");
    }
    if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={wrapperRef} style={{position: "relative"}}>
      <input
        className="input"
        placeholder={placeholder}
        disabled={disabled}
        value={open ? query : display}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKey}
        data-testid={testid}
      />
      {value && !open && (
        <button type="button" onClick={() => { onChange("", null); setQuery(""); }}
          style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",border:"none",background:"transparent",cursor:"pointer",color:"var(--mv-text-muted)"}}>×</button>
      )}
      {open && options.length > 0 && (
        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"white",
          border:"1px solid var(--mv-border)",borderRadius:8,maxHeight:280,overflow:"auto",zIndex:30,
          boxShadow:"0 6px 20px rgba(0,0,0,0.08)"}}
          data-testid={`${testid}-dropdown`}>
          {options.map((o, i) => (
            <div key={o.value} onClick={() => { onChange(o.value, o); setOpen(false); setQuery(""); }}
              style={{padding:"10px 12px",cursor:"pointer",fontSize:14,
                background: i === highlighted ? "var(--mv-bg)" : "transparent",
                borderBottom: i < options.length - 1 ? "1px solid var(--mv-border)" : "none"}}
              onMouseEnter={()=>setHighlighted(i)}>
              <div style={{fontWeight: 500}}>{o.label}</div>
              {o.subtitle && <div className="text-small text-muted" style={{marginTop:2}}>{o.subtitle}</div>}
            </div>
          ))}
        </div>
      )}
      {open && query.length >= minChars && options.length === 0 && (
        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"white",
          border:"1px solid var(--mv-border)",borderRadius:8,padding:"10px 12px",zIndex:30,
          fontSize:13,color:"var(--mv-text-muted)"}}>
          Nessun risultato per "{query}"
        </div>
      )}
    </div>
  );
}
